import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { selectRouteValues } from "../../components/ReservaTour/ReservaRecorrido/selectRouteValues";
import { Reservas } from "../../services/reservasCalls";
import { IProps, OwnerData } from "../../types/reservas";

const initialState: IProps = {
  reserva: {
    id: "",
    date: "",
    date_id: 0,
    hour: "",
    recorrido: selectRouteValues,
    tour_paths: [],
    owner: {
      institution: "",
      cue: "",
      state_id: 0,
      city: "",
      participants: 0,
      grade_id: 0,
    },
  },
  currentStep: 0,
  error: false,
  loading: false,
  success: false,
};

interface MakeReservationParams {
  date_id: number; // TourDayHour record ID
  tour_id: number;
  token: string | null;
  city: string;
  cue?: string;
  grade_id: number | string;
  institution?: string;
  participants: number | string;
  state_id: number | string;
  tour_paths?: number[];
}

export const makeReservation = createAsyncThunk(
  "reservas/make-reservation",
  async (values: MakeReservationParams, { rejectWithValue }) => {
    try {
      const bodyParameters = {
        date: values.date_id, // Send the TourDayHour record ID
        participants: Number(values.participants),
        grade_id: Number(values.grade_id),
        state_id: Number(values.state_id),
        city: values.city,
        tour_id: values.tour_id,
        ...(values.institution && { institution: values.institution }),
        ...(values.cue && { cue: values.cue }),
        ...(values.tour_paths && { tour_paths: values.tour_paths }),
      };

      const response = await Reservas.makeReservationCall(bodyParameters);

      if (response.data.message === "The Date is Reserved") {
        return rejectWithValue("La fecha esta reservada");
      }

      return response;
    } catch (error) {
      throw rejectWithValue(
        "Ocurrió un error inesperado, intente nuevamente mas tarde: " + error
      );
    }
  }
);

const reservasSlice = createSlice({
  name: "reservas",
  initialState,
  reducers: {
    startLoading: state => {
      state.loading = true;
    },
    setHourReserva: (
      state,
      action: { payload: { hour: string; date: string } }
    ) => {
      state.reserva.hour = action.payload.hour;
      state.reserva.date = action.payload.date;
    },
    setDateIdReserva: (state, action: { payload: number }) => {
      state.reserva.date_id = action.payload;
    },
    setDateReserva: (state, action: { payload: string }) => {
      state.reserva.date = action.payload;
    },
    setOwnerReserva: (
      state,
      action: { payload: typeof initialState.reserva.owner }
    ) => {
      state.reserva.owner = action.payload;
    },
    setRecorrido: (state, action: { payload: number[] }) => {
      state.reserva.tour_paths = action.payload;
    },
    setSelectRecorrido: (
      state,
      action: { payload: typeof initialState.reserva.recorrido }
    ) => {
      state.reserva.recorrido = action.payload;
    },
    setReserva: (state, action: { payload: typeof initialState.reserva }) => {
      state.loading = false;
      state.reserva = action.payload;
    },
    setErrorTrue: state => {
      state.error = true;
    },
    setErrorFalse: state => {
      state.error = false;
    },
    setSuccessFalse: state => {
      state.success = false;
    },
    nextStep: state => {
      state.currentStep = state.currentStep + 1;
    },
    previousStep: state => {
      state.currentStep = state.currentStep - 1;
    },
    setStep: (state, action: { payload: number }) => {
      state.currentStep = action.payload;
    },
    setInitiState: state => {
      state.currentStep = initialState.currentStep;
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.reserva = initialState.reserva;
      state.success = initialState.success;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(makeReservation.pending, state => {
        state.loading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(makeReservation.fulfilled, state => {
        state.loading = false;
        state.success = true;
      })
      .addCase(makeReservation.rejected, state => {
        state.loading = false;
        state.error = true;
        state.success = false;
      });
  },
});

export const {
  startLoading,
  setReserva,
  setErrorTrue,
  setDateReserva,
  setDateIdReserva,
  setHourReserva,
  setOwnerReserva,
  nextStep,
  previousStep,
  setStep,
  setRecorrido,
  setSuccessFalse,
  setErrorFalse,
  setInitiState,
  setSelectRecorrido,
} = reservasSlice.actions;

export default reservasSlice.reducer;
